import React, { useRef, useState, useEffect } from 'react';
import { Button, Flex, Input, Text } from "@chakra-ui/react";
import { PayRentPropTypes } from "../PayRentPropTypes";
import { Form } from "react-final-form";
import { generateYearArray, renderDropDown, renderEnableAutoPayCheckbox, renderExpirationDropdown, renderStateDropdown } from "../Utils";
import { FormField } from "../../../components/form";
import { emailFormButtonProps, month, payRentMainContainerProps, statesList } from "../constants";
import { autoPayWorldPayConfig1, autoPayWorldPayConfig2 } from "../config";
import { localisable } from "../../../generic/localisable";

export const AutoPayWorldPay = ({
    cardList,
    existingCardDetails,
    updateAutoPayHandler,
    logOffHandler,
    shallShowCVV,
    shallRenderCardIcons,
    IsAutoPaymentsEnabled,
    IsAutoPaymentsForced,
    category = 'option-01',
    tenantDetails,
    shallShowExpirationBlock,
    PreferredPaymentMethod,
    IsOptingOutAutoPaymentRestricted
}: PayRentPropTypes.ICombinedPayRentProps) => {

    const formProps = useRef({});
    const { CCNumber, CCBillingAccountName, CCExpirationMonth, CCExpirationYear, CCBillingAddress, CCBillingZIP, CCCvv } = existingCardDetails || {};
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
    const [payTypeDescription, setPayTypeDescription] = useState('');

    const { FirstName, AddressLine1, AddressLine2, City, State, ZIP } = tenantDetails || {};

    useEffect(() => {
        const selectedPayType = cardList.find(({ PayTypeID }: any) => PayTypeID == selectedPaymentMode);

        if (selectedPayType) {
            setPayTypeDescription(selectedPayType.PayTypeDescription);
        }
    }, [selectedPaymentMode]);

    const initialValuesOption1 = {
        cardHolderName: CCBillingAccountName || '',
        cardType: PreferredPaymentMethod || '',
        cardNumber: CCNumber || '',
        cvv: CCCvv || '',
        address: CCBillingAddress || '',
        zipCode: CCBillingZIP || '',
        expirationMonth: CCExpirationMonth || '',
        expirationYear: CCExpirationYear || '',
        amountTypeOpted: 'balanceDue',
        enableAutoPayments: IsAutoPaymentsEnabled || IsAutoPaymentsForced || false,
    };

    const initialValuesOption2 = {
        firstName: FirstName || '',
        cardType: PreferredPaymentMethod || '',
        cardNumber: CCNumber || '',
        cvv: CCCvv || '',
        expirationMonth: CCExpirationMonth || '',
        expirationYear: CCExpirationYear || '',
        addressLine1: AddressLine1 || '',
        addressLine2: AddressLine2 || '',
        zipCode: ZIP || '',
        city: City || '',
        state: State || '',
        amountTypeOpted: 'balanceDue',
        enableAutoPayments: IsAutoPaymentsEnabled || IsAutoPaymentsForced || false,
    };

    const onSubmit = () => {

        if (category === 'option-01') {

            const { cardHolderName, cardNumber, cvv, enableAutoPayments, cardType, expirationMonth, CCExpirationYear,
                address, zipCode } = (formProps.current as any).values || {};

            const payload = {
                objPayment: {
                    CCAccountNumber: cardNumber,
                    CCAccountName: cardHolderName,
                    CCExpirationMonth: expirationMonth,
                    CCExpirationYear: CCExpirationYear,
                    CCAccountCVV2: cvv,
                    CCAccountBillingAddress: address,
                    CCAccountZIP: zipCode,
                    SignUpForAutoPay: enableAutoPayments,
                    PayType: {
                        PayTypeID: cardType,
                        payTypeDescription: payTypeDescription,
                    },
                },
                blnGenerateDocs: true,
            };
            updateAutoPayHandler(payload);

        } else if (category === 'option-02') {

            const { firstName, cardNumber, cvv, enableAutoPayments, cardType, expirationMonth, expirationYear,
                addressLine1, addressLine2, zipCode, city, state, } = (formProps.current as any).values || {};

            const payload = {
                objPayment: {
                    CCAccountNumber: cardNumber,
                    CCAccountName: firstName,
                    CCExpirationMonth: expirationMonth,
                    CCExpirationYear: expirationYear,
                    CCAccountCVV2: cvv,
                    CCAccountBillingAddress: [addressLine1, addressLine2, city, state].join(', '),
                    CCAccountZIP: zipCode,
                    SignUpForAutoPay: enableAutoPayments,
                    PayType: {
                        PayTypeID: cardType,
                        payTypeDescription: payTypeDescription,
                    },
                },
                blnGenerateDocs: true,
            };
            updateAutoPayHandler(payload);
        }
    };

    const renderButtonSection = () => {
        return (
            <Flex direction={{ base: 'column', sm: 'row' }} gap='16px' justifyContent={{ sm: 'flex-end' }}>
                <Button
                    {...emailFormButtonProps}
                    type='submit'
                    bg='button.blue'
                    minW={{ base: '100%', sm: '204px' }}
                >
                    <Text textStyle='buttonSmall' color='common.white' >{localisable.makePayment}</Text>
                </Button>
                <Button
                    {...emailFormButtonProps}
                    onClick={() => logOffHandler()}
                    bg='button.blue'
                    minW={{ base: '100%', sm: '204px' }}
                >
                    <Text textStyle='buttonSmall' color='common.white' >{localisable.logOff}</Text>
                </Button>
            </Flex>
        )
    };

    const renderForm1 = (currentFormProps: any) => {

        const { nameProps, addressProps, zipCodeProps, cardNumberProps, cvvProps, expirationMonthProps,
            expirationYearProps, enableAutopayProps } = autoPayWorldPayConfig1() || {};

        return (
            <Flex direction='column' w='100%' padding={{ sm: '0px 96px' }} gap='24px'>
                <Text textStyle='h2' mb='8px'>{localisable.autoPaySignup}</Text>
                <FormField
                    required
                    name="cardType"
                    label=''
                    {...currentFormProps}
                >
                    {renderDropDown(true, currentFormProps, cardList, shallRenderCardIcons, undefined,
                        setSelectedPaymentMode, 0, undefined, undefined)}
                </FormField>
                <FormField {...nameProps} {...currentFormProps} >
                    <Input />
                </FormField>
                <FormField {...addressProps} {...currentFormProps} >
                    <Input />
                </FormField>
                <FormField {...zipCodeProps} {...currentFormProps} >
                    <Input />
                </FormField>
                <Flex direction={{ base: 'column', sm: 'row' }} gap='16px'>
                    <FormField {...cardNumberProps} {...currentFormProps} >
                        <Input />
                    </FormField>
                    {shallShowCVV && <FormField {...cvvProps} {...currentFormProps} >
                        <Input />
                    </FormField>}
                </Flex>
                <Flex gap='16px' alignItems='flex-end'>
                    <FormField {...expirationMonthProps} {...currentFormProps}>
                        {renderExpirationDropdown(month, currentFormProps.values.expirationMonth)}
                    </FormField>
                    <FormField {...expirationYearProps} {...currentFormProps}>
                        {renderExpirationDropdown(generateYearArray(), currentFormProps.values.expirationYear)}
                    </FormField>
                </Flex>
                <FormField {...enableAutopayProps} {...currentFormProps}>
                    {renderEnableAutoPayCheckbox(localisable.autoPaymentContent, IsAutoPaymentsForced, IsOptingOutAutoPaymentRestricted, currentFormProps.values.enableAutoPayments)}
                </FormField>
                {renderButtonSection()}
            </Flex >
        )
    }

    const renderForm2 = (currentFormProps: any) => {

        const { nameProps, addressLine1Props, addressLine2Props, zipCodeProps, cardNumberProps, cvvProps,
            cityProps, stateProps, expirationMonthProps, expirationYearProps, enableAutopayProps } = autoPayWorldPayConfig2() || {};

        return (
            <Flex direction='column' w='100%' padding={{ sm: '0px 96px' }} gap='24px'>
                <Text textStyle='h2' mb='8px'>{localisable.autoPaySignup}</Text>
                <FormField
                    required
                    name="cardType"
                    label=''
                    {...currentFormProps}
                >
                    {renderDropDown(true, currentFormProps, cardList, shallRenderCardIcons, undefined,
                        setSelectedPaymentMode, 0, undefined, undefined)}
                </FormField>
                <FormField {...enableAutopayProps} {...currentFormProps}>
                    {renderEnableAutoPayCheckbox(localisable.autoPaymentContent, IsAutoPaymentsForced, IsOptingOutAutoPaymentRestricted, currentFormProps.values.enableAutoPayments)}
                </FormField>
                <FormField {...nameProps} {...currentFormProps} >
                    <Input />
                </FormField>
                <Flex direction={{ base: 'column', sm: 'row' }} gap='16px'>
                    <FormField {...cardNumberProps} {...currentFormProps} >
                        <Input />
                    </FormField>
                    {shallShowCVV && <FormField {...cvvProps} {...currentFormProps} >
                        <Input />
                    </FormField>}
                </Flex>
                {shallShowExpirationBlock && <Flex gap='16px' alignItems='flex-end'>
                    <FormField {...expirationMonthProps} {...currentFormProps}>
                        {renderExpirationDropdown(month, currentFormProps.values.expirationMonth)}
                    </FormField>
                    <FormField {...expirationYearProps} {...currentFormProps}>
                        {renderExpirationDropdown(generateYearArray(), currentFormProps.values.expirationYear)}
                    </FormField>
                </Flex>}
                <FormField {...addressLine1Props} {...currentFormProps} >
                    <Input />
                </FormField>
                <FormField {...addressLine2Props} {...currentFormProps} >
                    <Input />
                </FormField>
                <Flex gap={{ base: '24px', sm: '32px' }} direction={{ base: 'column', sm: 'row' }} alignItems={{ sm: 'flex-start' }}>
                    <FormField {...cityProps} {...currentFormProps} >
                        <Input />
                    </FormField>
                    <Flex gap={{ base: '24px', sm: '32px' }}>
                        <FormField {...stateProps} {...currentFormProps}>
                            {renderStateDropdown(statesList, currentFormProps.values.state)}
                        </FormField>
                        <FormField {...zipCodeProps} {...currentFormProps} >
                            <Input />
                        </FormField>
                    </Flex>
                </Flex>
                {renderButtonSection()}
            </Flex >
        )
    }

    return (
        <Flex direction='column' {...payRentMainContainerProps} gap='24px'>
            <Form
                onSubmit={onSubmit}
                initialValues={category === 'option-01' ? initialValuesOption1 : initialValuesOption2}
                render={(currentFormProps) => {
                    const { handleSubmit, ...restFormProps } = currentFormProps;
                    formProps.current = restFormProps;
                    return (
                        <form onSubmit={handleSubmit}>
                            {category === 'option-01' ? renderForm1(currentFormProps) : renderForm2(currentFormProps)}
                        </form>
                    )
                }}
            />
        </Flex>
    );
};